import React, { createContext, useState, useCallback, useMemo, useEffect } from "react";
import { BEZPIECZNIK_LADOWANIA_MS } from "../components/common/Ladowanie/czasLadowania";

/*
 * [S19 2026-09-14] LICZNIK AKTYWNYCH ŁADOWAŃ zamiast jednego booleana.
 *
 * BYŁO ŹLE: `isLoading` było jednym booleanem. Dwa równoległe ładowania na jednym ekranie (np. lista zgłoszeń
 * i słowniki) gasiły się nawzajem — pierwsze `hideLoader()` chowało wskaźnik, choć drugie zapytanie jeszcze
 * trwało. Nie było też znacznika początku, więc nie dało się pokazać czasu trwania, o który prosił właściciel.
 * NAPRAWA: `showLoader()` zwiększa licznik, `hideLoader()` zmniejsza (nigdy poniżej zera), `isLoading` = licznik > 0.
 * API bez zmian dla wszystkich wołających; `startedAt` = początek pierwszego z trwających ładowań.
 *
 * BLOKADA TYLKO NA ŻYCZENIE: globalny wskaźnik INFORMUJE (pasek u góry + pigułka z czasem) i nie zasłania pracy.
 * Ekran, który naprawdę musi zablokować klikanie na czas operacji, woła `showLoader({ blokuj: true })`
 * i DOMYKA parą `hideLoader({ blokuj: true })`.
 *
 * BEZPIECZNIK: po BEZPIECZNIK_LADOWANIA_MS ciągłego ładowania licznik się zeruje z ostrzeżeniem w konsoli —
 * zgubione `hideLoader` nie może zostawić paska na ekranie na zawsze.
 */
const STAN_BEZCZYNNY = Object.freeze({ aktywne: 0, blokujace: 0, startedAt: null });

/** `showLoader(zdarzenie)` z `onClick={showLoader}` nie może udawać prośby o blokadę — liczy się tylko jawne `blokuj: true`. */
const czyBlokujace = (opcje) => !!(opcje && typeof opcje === "object" && opcje.blokuj === true);

export const LoadingContext = createContext({
  isLoading: false,
  blokuje: false,
  startedAt: null,
  showLoader: () => {},
  hideLoader: () => {},
});

export const LoadingProvider = ({ children }) => {
  const [stan, setStan] = useState(STAN_BEZCZYNNY);

  const showLoader = useCallback((opcje) => {
    const blokuj = czyBlokujace(opcje);
    setStan((s) => ({
      aktywne: s.aktywne + 1,
      blokujace: s.blokujace + (blokuj ? 1 : 0),
      startedAt: s.aktywne > 0 && s.startedAt != null ? s.startedAt : Date.now(),
    }));
  }, []);

  const hideLoader = useCallback((opcje) => {
    const blokuj = czyBlokujace(opcje);
    setStan((s) => {
      if (s.aktywne <= 0) return s === STAN_BEZCZYNNY ? s : STAN_BEZCZYNNY;
      const aktywne = s.aktywne - 1;
      if (aktywne === 0) return STAN_BEZCZYNNY;
      return { aktywne, blokujace: Math.max(0, s.blokujace - (blokuj ? 1 : 0)), startedAt: s.startedAt };
    });
  }, []);

  useEffect(() => {
    if (stan.startedAt == null) return undefined;
    const minutnik = window.setTimeout(() => {
      // eslint-disable-next-line no-console
      console.warn(
        `[LoadingContext] Wskaźnik ładowania trwał ponad ${Math.round(BEZPIECZNIK_LADOWANIA_MS / 1000)} s — licznik wyzerowany ` +
        "(ekran zgubił parę showLoader/hideLoader albo operacja trwa nietypowo długo)."
      );
      setStan(STAN_BEZCZYNNY);
    }, BEZPIECZNIK_LADOWANIA_MS);
    return () => window.clearTimeout(minutnik);
  }, [stan.startedAt]);

  const isLoading = stan.aktywne > 0;
  const blokuje = stan.blokujace > 0;
  const { startedAt } = stan;

  const value = useMemo(
    () => ({
      isLoading,
      blokuje,
      startedAt,
      showLoader,
      hideLoader,
    }),
    [isLoading, blokuje, startedAt, showLoader, hideLoader]
  );

  return (
    <LoadingContext.Provider value={value}>{children}</LoadingContext.Provider>
  );
};
